/**
 * Scroll Animations using GSAP ScrollTrigger
 */

export class ScrollAnimations {
  constructor() {
    this.init();
  }

  /**
   * Initialize animations
   */
  init() {
    if (
      typeof gsap === "undefined" ||
      typeof ScrollTrigger === "undefined" ||
      typeof ScrollToPlugin === "undefined" ||
      typeof SplitText === "undefined"
    ) {
      console.warn(
        "GSAP, ScrollTrigger, ScrollToPlugin, or SplitText not loaded",
      );
      return;
    }

    gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, SplitText);
    this.mm = gsap.matchMedia();

    this.logoText();
    this.popupVideos();
    this.stats();
    this.features();
    this.highlights();

    this.problemSolution();
    this.featuresTabs();
    this.contentModuleScroll();
    this.reasonsTimeline();
    this.megaMenuPlatform();
    this.faqAccordion();
    this.fullscreenMenu();
  }

  fullscreenMenu() {
    const menu = document.querySelector(".wps-fullscreen-menu");
    const toggleBtn = document.querySelector("[data-toggle-btn]");
    const items = document.querySelectorAll(".menu__item.has-sub");

    if (!menu || !toggleBtn) return;

    let isOpen = false;

    const handleEsc = (e) => {
      if (e.key === "Escape" && isOpen) {
        closeMenu();
      }
    };

    function closeMenu() {
      isOpen = false;

      menu.classList.remove("active");
      toggleBtn.classList.remove("active");
      document.body.style.overflow = "";

      items.forEach((item) => {
        item.classList.remove("active");

        const dropdown = item.querySelector(".menu__dropdown");
        if (dropdown) {
          gsap.to(dropdown, {
            height: 0,
            duration: 0.25,
            ease: "power2.out",
          });
        }
      });

      document.removeEventListener("keydown", handleEsc);
    }

    function openMenu() {
      isOpen = true;

      menu.classList.add("active");
      toggleBtn.classList.add("active");
      document.body.style.overflow = "hidden";

      document.addEventListener("keydown", handleEsc);
    }

    toggleBtn.addEventListener("click", () => {
      isOpen ? closeMenu() : openMenu();
    });

    items.forEach((item) => {
      const btn = item.querySelector(".menu__link");
      const dropdown = item.querySelector(".menu__dropdown");

      if (!btn || !dropdown) return;

      gsap.set(dropdown, { height: 0, overflow: "hidden" });

      btn.addEventListener("click", () => {
        const isActive = item.classList.contains("active");

        items.forEach((i) => {
          i.classList.remove("active");

          const d = i.querySelector(".menu__dropdown");
          if (d) {
            gsap.to(d, {
              height: 0,
              duration: 0.25,
              ease: "power2.out",
            });
          }
        });

        if (!isActive) {
          item.classList.add("active");

          gsap.to(dropdown, {
            height: dropdown.scrollHeight,
            duration: 0.25,
            ease: "power2.out",
          });
        }
      });
    });
  }

  faqAccordion() {
    const section = document.querySelector(".wps-insidepage-faq");
    if (!section) return;

    const items = section.querySelectorAll(".faq__item");

    items.forEach((item) => {
      const question = item.querySelector(".faq__question");
      const answer = item.querySelector(".faq__answer");

      gsap.set(answer, { height: 0 });

      question.addEventListener("click", () => {
        const isActive = item.classList.contains("active");

        items.forEach((i) => {
          const a = i.querySelector(".faq__answer");

          i.classList.remove("active");

          gsap.to(a, {
            height: 0,
            duration: 0.2,
            ease: "none",
          });
        });

        if (!isActive) {
          item.classList.add("active");

          const fullHeight = answer.scrollHeight;

          gsap.fromTo(
            answer,
            { height: 0 },
            {
              height: fullHeight,
              duration: 0.2,
              ease: "none",
            },
          );
        }
      });
    });
  }

  logoText() {
    const header = document.querySelector("header");
    const logoText = document.querySelector(".logo__text");

    if (header && logoText) {
      gsap.to(logoText, {
        y: -40,
        opacity: 0,
        duration: 0.5,
        ease: "power2.out",
        scrollTrigger: {
          trigger: header,
          start: "bottom center",
          end: "bottom center",
          toggleActions: "play none none reverse",
        },
      });
    }
  }

  popupVideos() {
    const popup = document.getElementById("videoPopup");
    if (!popup) return;

    const modal = popup.querySelector(".video-popup__modal");
    const overlay = popup.querySelector(".video-popup__overlay");
    const iframeContainer = popup.querySelector(".video-popup__iframe");
    const closeBtn = popup.querySelector(".video-popup__close");

    function getEmbedUrl(url) {
      if (!url) return null;

      const youtube = url.match(
        /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^?&]+)/,
      );
      if (youtube) {
        return {
          type: "youtube",
          url: `https://www.youtube.com/embed/${youtube[1]}?autoplay=1&rel=0`,
        };
      }

      const vimeo = url.match(/vimeo\.com\/(\d+)/);
      if (vimeo) {
        return {
          type: "vimeo",
          url: `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`,
        };
      }

      if (url.endsWith(".mp4")) {
        return {
          type: "mp4",
          url: url,
        };
      }

      return null;
    }

    function openVideo(url) {
      const video = getEmbedUrl(url);
      if (!video) return;

      if (video.type === "mp4") {
        iframeContainer.innerHTML = `
        <video controls autoplay>
          <source src="${video.url}" type="video/mp4">
        </video>
      `;
      } else {
        iframeContainer.innerHTML = `
        <iframe src="${video.url}"
          allow="autoplay; fullscreen"
          allowfullscreen>
        </iframe>
      `;
      }

      popup.classList.add("active");

      gsap.to(overlay, {
        opacity: 1,
        duration: 0.3,
      });

      gsap.to(modal, {
        opacity: 1,
        scale: 1,
        duration: 0.4,
        ease: "power3.out",
      });
    }

    function closeVideo() {
      gsap.to(modal, {
        opacity: 0,
        scale: 0.9,
        duration: 0.3,
      });

      gsap.to(overlay, {
        opacity: 0,
        duration: 0.3,
        onComplete: () => {
          popup.classList.remove("active");
          iframeContainer.innerHTML = "";
        },
      });
    }

    document.querySelectorAll("[data-video-url]").forEach((card) => {
      const btn = card.querySelector("[data-play-btn]");

      btn?.addEventListener("click", () => {
        const url = card.dataset.videoUrl;

        if (!url) return;

        openVideo(url);
      });
    });

    closeBtn.addEventListener("click", closeVideo);
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        closeVideo();
      }
    });
  }

  stats() {
    const section = document.querySelector("[data-stats]");
    if (!section) return;

    const cardsContainer = section.querySelector(".stats__cards");
    const cards = section.querySelectorAll("[data-stat-card]");
    const numbers = section.querySelectorAll("[data-stat-number]");

    if (cardsContainer) {
      gsap.from(cardsContainer, {
        borderTopWidth: 0,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: cardsContainer,
          start: "top 85%",
          once: true,
        },
      });
    }

    cards.forEach((card, index) => {
      gsap.from(card, {
        y: 50,
        opacity: 0,
        duration: 0.8,
        delay: index * 0.15,
        ease: "power3.out",
        scrollTrigger: {
          trigger: card,
          start: "top 80%",
          once: true,
        },
      });
    });

    numbers.forEach((num) => {
      const raw = num.textContent.trim();

      const value = parseFloat(raw.replace(/[^\d.]/g, ""));

      const suffix = raw.replace(/[\d.]/g, "");

      gsap.fromTo(
        num,
        { innerText: 0 },
        {
          innerText: value,
          duration: 1.4,
          ease: "power1.out",
          snap: { innerText: 0.01 },
          scrollTrigger: {
            trigger: num,
            start: "top 85%",
            once: true,
          },
          onUpdate() {
            let current = parseFloat(num.innerText);

            if (value % 1 !== 0) {
              current = current.toFixed(1);
            } else {
              current = Math.floor(current).toLocaleString();
            }

            num.innerText = current + suffix;
          },
        },
      );
    });
  }

  features() {
    const section = document.querySelector(".wps-homepage-features");
    if (!section) return;

    const container = section.querySelector(".features__tabs");
    const tabList = container.querySelector(".tab__lists");

    const cards = gsap.utils.toArray(".cards__container .card");
    const buttons = gsap.utils.toArray(".tab__lists button");

    const cardColors = [
      { bg: "#fdeede", stroke: "#ff7031" },
      { bg: "#e4f9e9", stroke: "#2fd286" },
      { bg: "#eee3fd", stroke: "#6105a3" },
      { bg: "#e1f6ff", stroke: "#60d0ff" },
      { bg: "#fdeede", stroke: "#ff7031" },
    ];

    ScrollTrigger.create({
      trigger: container,
      start: "top 64px",
      end: "bottom bottom",
      pin: tabList,
      pinSpacing: false,
      invalidateOnRefresh: true,
    });

    cards.forEach((card, i) => {
      const color = cardColors[i];
      if (!color) return;

      card.style.setProperty("--card-bg", color.bg);
      card.style.setProperty("--icon-stroke", color.stroke);

      buttons[i]?.style.setProperty("--icon-stroke", color.stroke);
    });

    this.mm.add("(min-width:991px)", () => {
      const setActive = (index) => {
        buttons.forEach((btn, i) =>
          btn.classList.toggle("active", i === index),
        );
      };

      const scrollToCard = (target) => {
        const distance = Math.abs(target - lenis.scroll);

        lenis.scrollTo(target, {
          duration: Math.min(2, distance / 2000),
          easing: (t) => t,
        });
      };

      buttons.forEach((btn, i) => {
        btn.addEventListener("click", () => {
          if (btn.classList.contains("active")) return;

          const trigger = ScrollTrigger.getById(`card-${i}`);

          // scrollToCard(trigger.start + 32);

          gsap.to(window, {
            scrollTo: trigger.start + 100,
            duration: 0.8,
            ease: "power2.out",
            overwrite: "auto",
          });
        });
      });

      cards.forEach((card, index) => {
        ScrollTrigger.create({
          id: `card-${index}`,
          trigger: card,
          start: "top 40%",
          end: "bottom center",
          onToggle: ({ isActive }) => {
            if (isActive) setActive(index);
          },
        });

        if (index === 0) return;

        const prevCards = cards.slice(0, index);

        gsap
          .timeline({
            scrollTrigger: {
              trigger: card,
              start: "top 60%",
              end: "top 240px",
              scrub: true,
            },
          })
          .to(prevCards, {
            scale: (_, el) => 1 - (index - cards.indexOf(el)) * 0.06,
            y: (_, el) => -(index - cards.indexOf(el)) * 60,
            opacity: (_, el) => (index - cards.indexOf(el) >= 3 ? 0 : 1),
            ease: "none",
          });
      });
    });
  }

  highlights() {
    const section = document.querySelector(".wps-homepage-highlights");
    if (!section) return;

    const stats = gsap.utils.toArray(".stat-list");
    const contents = gsap.utils.toArray(".stat-description");
    const indicator = section.querySelector(".stat__indicator");
    const line = indicator.querySelector(".indicator__line");
    const coloredLine = indicator.querySelector(".indicator__colored-line");

    this.mm.add("(min-width:768px)", () => {
      let lineHeight = 0;

      gsap.set(contents, { opacity: 0, y: 20 });
      gsap.set(contents[0], { opacity: 1, y: 0 });

      stats[0].classList.add("active");

      function setIndicatorLine() {
        const circles = section.querySelectorAll(".indicator__circle");
        if (circles.length < 2) return;

        const container = section.querySelector(".stat__lists");

        const first = circles[0].getBoundingClientRect();
        const last = circles[circles.length - 1].getBoundingClientRect();
        const parent = container.getBoundingClientRect();

        const start = first.top + first.height / 2 - parent.top;
        const end = last.top + last.height / 2 - parent.top;

        lineHeight = end - start;

        line.style.top = `${start}px`;
        line.style.height = `${lineHeight}px`;

        coloredLine.style.top = `${start}px`;
        coloredLine.style.height = `0px`;
      }
      setIndicatorLine();

      window.addEventListener("load", setIndicatorLine);
      window.addEventListener("resize", setIndicatorLine);

      gsap.to(coloredLine, {
        scrollTrigger: {
          trigger: indicator,
          start: "top 65%",
          end: "bottom 65%",
          scrub: true,
        },
        height: () => lineHeight,
        ease: "none",
      });

      function showContent(index) {
        stats.forEach((s, i) => {
          s.classList.toggle("active", i === index);
        });

        contents.forEach((content, i) => {
          if (i === index) {
            gsap.to(content, {
              opacity: 1,
              y: 0,
              duration: 0.25,
              overwrite: true,
              ease: "none",
            });
          } else {
            gsap.to(content, {
              opacity: 0,
              y: -20,
              duration: 0.25,
              overwrite: true,
              ease: "none",
            });
          }
        });
      }

      stats.forEach((stat, index) => {
        ScrollTrigger.create({
          trigger: stat,
          start: "top 65%",
          end: "+=45px",
          onEnter: () => showContent(index),
          onEnterBack: () => showContent(index),
        });
      });
    });

    this.mm.add("(max-width: 767px)", () => {
      let lineHeight = 0;

      function setIndicatorLine() {
        const circles = section.querySelectorAll(".indicator__circle");
        if (circles.length < 2) return;

        const container = section.querySelector(".stat__lists");

        const first = circles[0].getBoundingClientRect();
        const last = circles[circles.length - 1].getBoundingClientRect();
        const parent = container.getBoundingClientRect();

        const start = first.top + first.height / 2 - parent.top;
        const end = last.top + last.height / 2 - parent.top;

        lineHeight = end - start;

        line.style.top = `${start}px`;
        line.style.height = `${lineHeight}px`;

        coloredLine.style.top = `${start}px`;
        coloredLine.style.height = `0px`;
      }
      setIndicatorLine();

      window.addEventListener("load", setIndicatorLine);
      window.addEventListener("resize", setIndicatorLine);

      stats.forEach((stat) => stat.classList.add("active"));

      contents.forEach((content) => {
        gsap.set(content, { opacity: 1, y: 0 });
      });
    });
  }

  problemSolution() {
    const section = document.querySelector(".wps-insidepage-problem-solution");
    if (!section) return;

    this.mm.add("(min-width: 991px)", () => {
      const images = section.querySelectorAll(".sticky__image");
      const problem = images[0];
      const solution = images[1];

      const problemIcon = problem.querySelector(".icon");
      const solutionIcon = solution.querySelector(".icon");

      const trigger = section.querySelector(".scroll__content:nth-child(2)");

      gsap.set(solution, { opacity: 0, scale: 0.95 });
      gsap.set(solutionIcon, { opacity: 0, scale: 0.8, rotate: -30 });

      const tl = gsap.timeline({ paused: true });

      tl.to(problem, {
        opacity: 0,
        scale: 0.95,
        duration: 0.4,
        ease: "power2.out",
      })
        .to(
          problemIcon,
          {
            opacity: 0,
            scale: 0.8,
            rotate: 15,
            duration: 0.2,
            ease: "power2.out",
          },
          "<",
        )
        .to(
          solution,
          {
            opacity: 1,
            scale: 1,
            duration: 0.4,
            ease: "power2.out",
          },
          "-=0.1",
        )
        .to(
          solutionIcon,
          {
            opacity: 1,
            scale: 1,
            rotate: 0,
            duration: 0.3,
            ease: "back.out(1.7)",
          },
          "+=0.15",
        );

      ScrollTrigger.create({
        trigger: trigger,
        start: "top center",
        end: "bottom center",
        onEnter: () => tl.play(),
        onLeaveBack: () => tl.reverse(),
      });
    });
  }

  featuresTabs() {
    const section = document.querySelector(".wps-insidepage-features-tabs");
    if (!section) return;

    const tabs = section.querySelectorAll(".tab__title");
    const contents = section.querySelectorAll(".tab__content");

    this.mm.add("(min-width: 992px)", () => {
      const handleClick = (tab) => {
        const id = tab.getAttribute("data-tab-title");

        gsap.to(window, {
          scrollTo: { y: section, offsetY: -60 },
          duration: 0.8,
          ease: "power2.out",
        });

        tabs.forEach((t) => t.classList.remove("active"));
        contents.forEach((c) => c.classList.remove("active"));

        tab.classList.add("active");

        const activeContent = section.querySelector(
          `.tab__content[data-tab-content="${id}"]`,
        );

        activeContent.classList.add("active");

        gsap.fromTo(
          activeContent,
          { y: 40, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.4 },
        );
      };

      tabs.forEach((tab) => {
        tab.addEventListener("click", () => handleClick(tab));
      });

      if (tabs.length) tabs[0].classList.add("active");
      if (contents.length) contents[0].classList.add("active");
    });

    this.mm.add("(max-width: 991px)", () => {
      const accordion = section.querySelector(".features-accordion");
      if (!accordion) return;

      const titles = accordion.querySelectorAll(".tab__title");
      const contents = accordion.querySelectorAll(".tab__content");

      titles.forEach((title) => {
        title.addEventListener("click", () => {
          const content = title.nextElementSibling;
          const isActive = title.classList.contains("active");

          contents.forEach((c) => {
            gsap.killTweensOf(c);

            gsap.to(c, {
              height: 0,
              duration: 0.4,
              ease: "none",
              overwrite: true,
            });

            c.classList.remove("active");
          });

          titles.forEach((t) => t.classList.remove("active"));

          if (!isActive) {
            title.classList.add("active");
            content.classList.add("active");

            gsap.fromTo(
              content,
              { height: 0 },
              {
                height: content.scrollHeight,
                duration: 0.4,
                ease: "none",
                overwrite: true,
                onComplete: () => {
                  content.style.height = "auto";
                },
              },
            );

            gsap.delayedCall(0.1, () => {
              gsap.to(window, {
                scrollTo: {
                  y: title,
                  offsetY: 72,
                },
                duration: 0.6,
                ease: "power2.out",
              });
            });
          }
        });
      });

      // const firstTitle = accordion.querySelector(".tab__title");
      // const firstContent = accordion.querySelector(".tab__content");

      // if (firstTitle && firstContent) {
      //   firstTitle.classList.add("active");
      //   firstContent.classList.add("active");

      //   gsap.set(firstContent, {
      //     height: "auto",
      //   });
      // }
    });
  }

  megaMenuPlatform() {
    const header = document.querySelector(".wps-header-navbar");
    const trigger = header.querySelector(".navbar__menu ul li:first-child");
    const menu = header.querySelector(".navbar__mega-menu--platform");

    if (!trigger || !menu) return;

    let timeout;

    const show = () => {
      console.log("first");
      clearTimeout(timeout);
      header.classList.add("is-mega-open");
    };

    const hide = () => {
      timeout = setTimeout(() => {
        header.classList.remove("is-mega-open");
      }, 120);
    };

    trigger.addEventListener("mouseenter", show);
    menu.addEventListener("mouseenter", show);

    trigger.addEventListener("mouseleave", hide);
    menu.addEventListener("mouseleave", hide);
  }

  contentModuleScroll() {
    const section = document.querySelector(".wps-insidepage-content-module-02");
    if (!section) return;

    this.mm.add("(min-width: 991px)", () => {
      const container = section.querySelector(".section__bottom");
      const items = section.querySelectorAll(".content__item");
      const images = section.querySelectorAll(".image__item");
      const indicators = section.querySelectorAll(".section__indicator");
      const dots = section.querySelectorAll(".dot");
      const left = section.querySelector(".bottom__image");

      ScrollTrigger.create({
        trigger: container,
        start: "top top",
        end: "bottom bottom",
        pin: left,
        pinSpacing: false,
      });

      ScrollTrigger.create({
        trigger: container,
        start: "top top",
        end: "bottom bottom",
        pin: indicators,
        pinSpacing: false,
      });

      function activate(index) {
        images.forEach((img, i) => {
          img.classList.toggle("active", i === index);
        });

        dots.forEach((dot, i) => {
          dot.classList.toggle("active", i === index);
        });
      }

      items.forEach((item, index) => {
        ScrollTrigger.create({
          trigger: item,
          start: "top center",
          end: "bottom center",
          onEnter: () => activate(index),
          onEnterBack: () => activate(index),
        });
      });
    });
  }

  reasonsTimeline() {
    const section = document.querySelector(".wps-insidepage-process-module-01");
    if (!section) return;

    this.mm.add("(min-width:768px)", () => {
      const items = section.querySelectorAll(".reason__item");
      const timeline = section.querySelector(".timeline");
      const progress = section.querySelector(".timeline .progress");

      function setTimelineHeight() {
        const firstDot = items[0].querySelector(".dot");
        const lastDot = items[items.length - 1].querySelector(".dot");

        if (!firstDot || !lastDot) return;

        const timelineRect = timeline.getBoundingClientRect();
        const firstRect = firstDot.getBoundingClientRect();
        const lastRect = lastDot.getBoundingClientRect();

        const start = firstRect.top - timelineRect.top;
        const end = lastRect.top - timelineRect.top;

        timeline.style.top = start + "px";
        timeline.style.height = end - start + "px";
      }
      setTimelineHeight();
      window.addEventListener("resize", setTimelineHeight);

      gsap.to(progress, {
        height: "100%",
        ease: "none",
        scrollTrigger: {
          trigger: timeline,
          start: "top 70%",
          end: "bottom 70%",
          scrub: 0.8,
        },
      });

      items.forEach((item, i) => {
        item.classList.toggle("active", i === 0);
      });

      items.forEach((item) => {
        ScrollTrigger.create({
          trigger: item,
          start: "top 70%",
          end: "bottom 70%",
          onEnter: () => setActive(item),
          onEnterBack: () => setActive(item),
        });
      });

      function setActive(activeItem) {
        items.forEach((item) => {
          if (item !== activeItem) item.classList.remove("active");
        });

        activeItem.classList.add("active");
      }
    });

    this.mm.add("(max-width: 767px)", () => {
      const items = section.querySelectorAll(".reason__item");
      const timeline = section.querySelector(".timeline");
      const progress = section.querySelector(".timeline .progress");

      function setTimelineHeight() {
        const firstDot = items[0].querySelector(".dot");
        const lastDot = items[items.length - 1].querySelector(".dot");

        if (!firstDot || !lastDot) return;

        const timelineRect = timeline.getBoundingClientRect();
        const firstRect = firstDot.getBoundingClientRect();
        const lastRect = lastDot.getBoundingClientRect();

        const start = firstRect.top - timelineRect.top;
        const end = lastRect.top - timelineRect.top;

        timeline.style.top = start + "px";
        timeline.style.height = end - start + "px";
      }
      setTimelineHeight();
      window.addEventListener("resize", setTimelineHeight);
    });
  }
}
